import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import AuthShell from "../components/AuthShell";
import { getAuthErrorMessage } from "../utils/authErrors";

const Eye = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path
      d="M1.5 12s3.75-7.5 10.5-7.5S22.5 12 22.5 12 18.75 19.5 12 19.5 1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const EyeOff = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M1.5 12s3.75-7.5 10.5-7.5c2.1 0 3.98.62 5.6 1.62M22.5 12s-3.75 7.5-10.5 7.5c-2.1 0-3.98-.62-5.6-1.62"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M9.4 9.4a3.5 3.5 0 004.9 4.9"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const fieldClassName =
  "w-full rounded-2xl border border-[#ead7b0] bg-white/90 px-4 py-3 text-[#4e3520] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] outline-none transition placeholder:text-[#b39368] focus:border-[#c99649] focus:ring-2 focus:ring-[#efd8aa]";

const primaryButtonClassName =
  "w-full rounded-2xl bg-[linear-gradient(135deg,#a56a22,#d6a654)] px-4 py-3 text-sm font-semibold text-white shadow-[0_20px_40px_-24px_rgba(118,73,20,0.65)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userCred = await signInWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password
      );

      const userDoc = await getDoc(doc(db, "users", userCred.user.uid));
      if (!userDoc.exists()) {
        await signOut(auth);
        setError("No instructor record found for this account.");
        setLoading(false);
        return;
      }
      const data = userDoc.data();
      if (data.role !== "instructor") {
        await signOut(auth);
        setError("Access denied. This portal is for instructors only.");
        setLoading(false);
        return;
      }

      setLoading(false);
      navigate("/dashboard");
    } catch (err) {
      setError(getAuthErrorMessage(err, "login"));
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetError("");
    setResetMsg("");
    setResetEmail(form.email || "");
    setResetOpen(true);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetMsg("");
    const email = (resetEmail || "").trim();
    if (!email) {
      setResetError("Please enter your email.");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMsg(
        "If an account exists for this email, a password reset link has been sent."
      );
    } catch (err) {
      setResetError(getAuthErrorMessage(err, "resetPassword"));
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (!resetOpen) return;
    const onKey = (e) => e.key === "Escape" && setResetOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resetOpen]);

  return (
    <>
      <AuthShell
        portalLabel="Shop Owner Portal"
        badge="Welcome Back"
        title="Sign in to the Shop Owner Portal"
        description="Access listings, store tools, packages, and business insights with the same premium auth experience used across Ceylon Web Mall."
        footer={
          <>
            Need a shop owner account?{" "}
            <Link
              to="/register"
              className="font-semibold text-[#9b6b2f] transition hover:text-[#7e541f]"
            >
              Register here
            </Link>
          </>
        }
      >
        <div className="mx-auto w-full max-w-md">
          {error && (
            <div className="mb-4 rounded-2xl border border-[#e7b0ab] bg-[#fff4f3] px-4 py-3 text-sm text-[#9f3b2f]">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                className={fieldClassName}
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="********"
                  className={`${fieldClassName} pr-12`}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-2 my-auto rounded-xl p-2 text-[#8c6b46] transition hover:bg-[#fff5e1] hover:text-[#5b3d1d]"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={openReset}
                  className="text-sm font-medium text-[#9b6b2f] transition hover:text-[#7e541f]"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={primaryButtonClassName}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </AuthShell>

      {resetOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-[#2b1d0e]/55"
            onClick={() => setResetOpen(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md rounded-[28px] border border-[#ead7b0] bg-[linear-gradient(180deg,#fffdf8_0%,#fff5e6_100%)] p-6 shadow-[0_36px_80px_-40px_rgba(69,39,15,0.55)]">
              <button
                className="absolute right-4 top-3 text-2xl leading-none text-[#8c6b46] transition hover:text-[#5b3d1d]"
                aria-label="Close reset password dialog"
                onClick={() => setResetOpen(false)}
              >
                ×
              </button>

              <h3 className="mb-1 text-xl font-bold text-[#4e3520]">
                Reset password
              </h3>
              <p className="mb-4 text-sm text-[#7d6241]">
                Enter your email and we&apos;ll send you a password reset link.
              </p>

              {resetError && (
                <div className="mb-3 rounded-2xl border border-[#e7b0ab] bg-[#fff4f3] px-4 py-3 text-sm text-[#9f3b2f]">
                  {resetError}
                </div>
              )}
              {resetMsg && (
                <div className="mb-3 rounded-2xl border border-[#c8ddb3] bg-[#f4faee] px-4 py-3 text-sm text-[#527239]">
                  {resetMsg}
                </div>
              )}

              <form onSubmit={handleReset}>
                <label
                  htmlFor="reset-email"
                  className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
                >
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  className={`${fieldClassName} mb-4`}
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  required
                />

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-2xl border border-[#ead7b0] px-4 py-2 text-[#6c4d2c] transition hover:bg-[#fff8ec]"
                    onClick={() => setResetOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="rounded-2xl bg-[linear-gradient(135deg,#a56a22,#d6a654)] px-4 py-2 text-white transition hover:brightness-105 disabled:opacity-60"
                  >
                    {resetLoading ? "Sending..." : "Send reset link"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
